import React, { useState, useCallback } from 'react';
import { saveServiceRequest } from '../services/db';
import { notifyInternal, notifyCustomer } from '../services/email';
import { bookingConfirmationEmail } from '../services/emailTemplates';
import {
  Box,
  TextField,
  Button,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Typography,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';

// Services that require device + brand selection
const HARDWARE_SERVICES = ['Laptop Repair', 'Desktop Service', 'Mobile Device Repair'];

const allServices = [
  'Microsoft 365 & Azure Cloud',
  'Cybersecurity Services',
  'Network Installation',
  'Network Security',
  'Endpoint Management',
  'Server Administration',
  'Software Installation & Configuration',
  'VoIP & Phone Systems',
  'Laptop Repair',
  'Desktop Service',
  'Mobile Device Repair',
  'Technical Support',
  'Data Recovery',
  'WiFi Optimization',
  'Network Storage',
  'Website Development',
];

// Sub-options for each non-hardware service
const serviceDetails = {
  'Microsoft 365 & Azure Cloud': [
    'M365 Setup & Migration',
    'Azure AD / Entra ID',
    'Exchange Online',
    'SharePoint & OneDrive',
    'Microsoft Teams',
    'Intune / MDM',
    'Hybrid Cloud Configuration',
    'Other',
  ],
  'Cybersecurity Services': [
    'Firewall Configuration (ASA / Pfsense / SonicWall)',
    'Security Assessment',
    'Zero Trust Implementation',
    'NAC / Cisco ISE Setup',
    'Endpoint Security',
    'Other',
  ],
  'Network Installation': [
    'Home Network Setup',
    'Business Network Setup',
    'Wired (Ethernet) Network',
    'Wireless Network',
    'Wired + Wireless (Mixed)',
    'Structured Cabling',
    'Other',
  ],
  'Network Security': [
    'Firewall Setup',
    'VPN Configuration',
    'Intrusion Detection / IDS',
    'Network Audit',
    'Access Control',
    'Other',
  ],
  'Endpoint Management': [
    'Microsoft Intune Setup',
    'Windows Autopilot',
    'Device Compliance Policies',
    'MDM Enrollment',
    'App Deployment',
    'Other',
  ],
  'Server Administration': [
    'Windows Server',
    'Linux Server',
    'Active Directory / Group Policy',
    'VMware vSphere / ESXi',
    'Proxmox',
    'Backup & Recovery',
    'Other',
  ],
  'Software Installation & Configuration': [
    'Windows Deployment',
    'Linux Deployment',
    'macOS Setup',
    'Application Installation',
    'Automated Provisioning',
    'System Configuration',
    'Other',
  ],
  'VoIP & Phone Systems': [
    'Cisco CUCM',
    'Alcatel 4400 / OXO',
    'New System Installation',
    'Call Routing Configuration',
    'QoS Optimization',
    'Troubleshooting',
    'Other',
  ],
  'Technical Support': [
    'Remote Support',
    'On-site Support',
    'Software Issue',
    'Hardware Issue',
    'Network Issue',
    'Other',
  ],
  'Data Recovery': [
    'Hard Drive (HDD)',
    'Solid State Drive (SSD)',
    'USB / Flash Drive',
    'Memory Card',
    'RAID Array',
    'Other',
  ],
  'WiFi Optimization': [
    'Home WiFi',
    'Business WiFi',
    'Coverage / Dead Zone Issues',
    'Speed & Performance',
    'New Access Point Installation',
    'Other',
  ],
  'Network Storage': [
    'NAS Setup',
    'NAS Configuration',
    'Backup Solution',
    'Storage Expansion',
    'RAID Configuration',
    'Other',
  ],
  'Website Development': [
    'New Website',
    'Website Redesign',
    'E-commerce Store',
    'Landing Page',
    'Website Maintenance',
    'Other',
  ],
};

// Hardware service → device brands
const hardwareBrands = {
  'Laptop Repair': ['Apple', 'Dell', 'HP', 'Lenovo', 'ASUS', 'Acer', 'Microsoft', 'MSI', 'Samsung', 'Toshiba', 'Other'],
  'Desktop Service': ['Dell', 'HP', 'Lenovo', 'ASUS', 'Acer', 'Apple', 'Custom Built', 'Other'],
  'Mobile Device Repair': ['Apple', 'Samsung', 'Google', 'OnePlus', 'Xiaomi', 'Motorola', 'LG', 'Sony', 'Other'],
};

const mobileDeviceTypes = ['Smartphone', 'Tablet'];

const BookingForm = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    service: '',
    serviceDetail: '',
    mobileDeviceType: '',
    deviceBrand: '',
    deviceModel: '',
    issueDescription: '',
    preferredDate: '',
    preferredTime: '',
  });

  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const isHardware = HARDWARE_SERVICES.includes(formData.service);
  const isMobile = formData.service === 'Mobile Device Repair';

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'service' ? { serviceDetail: '', mobileDeviceType: '', deviceBrand: '', deviceModel: '' } : {}),
    }));
  }, []);

  const buildServiceSummary = () => {
    if (isHardware) {
      const parts = [formData.service];
      if (isMobile && formData.mobileDeviceType) parts.push(formData.mobileDeviceType);
      if (formData.deviceBrand) parts.push(formData.deviceBrand);
      if (formData.deviceModel) parts.push(formData.deviceModel);
      return parts.join(' — ');
    }
    const parts = [formData.service];
    if (formData.serviceDetail) parts.push(formData.serviceDetail);
    return parts.join(' — ');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      const baseRequired = ['firstName', 'lastName', 'email', 'phone', 'service', 'issueDescription', 'preferredDate', 'preferredTime'];
      const extraRequired = isHardware
        ? (isMobile ? ['mobileDeviceType', 'deviceBrand'] : ['deviceBrand'])
        : ['serviceDetail'];
      const missingFields = [...baseRequired, ...extraRequired].filter(f => !formData[f]);

      if (missingFields.length > 0) {
        throw new Error('Please fill in all required fields');
      }

      const summary = buildServiceSummary();
      const fullName = `${formData.firstName} ${formData.lastName}`;
      const ticketMessage =
        `Service: ${summary}\n` +
        `Description: ${formData.issueDescription}\n` +
        `Preferred Date: ${formData.preferredDate} at ${formData.preferredTime}\n` +
        `Phone: ${formData.phone}`;

      // ── 1. Create ticket in osTicket helpdesk ──────────────────────────
      let osTicketNumber = null;
      try {
        const apiUrl = process.env.NODE_ENV === 'production'
          ? '/api/create-ticket.php'
          : 'http://localhost:5000/api/create-ticket';
        const ticketRes = await fetch(apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: fullName,
            email: formData.email,
            phone: formData.phone,
            subject: `Service Booking — ${summary}`,
            message: ticketMessage,
          }),
        });
        const ticketData = await ticketRes.json();
        if (ticketData.success) {
          osTicketNumber = ticketData.ticketNumber;
        }
      } catch (e) {
        // Helpdesk unavailable — continue with email fallback
        console.warn('osTicket unavailable — booking saved locally only');
      }

      // ── 2. Save to Firestore + localStorage fallback ────────────────────
      const newRequest = {
        id: osTicketNumber || `SR-${Date.now().toString(36).toUpperCase()}`,
        date: formData.preferredDate,
        time: formData.preferredTime,
        customerName: fullName,
        email: formData.email,
        phone: formData.phone,
        service: formData.service,
        serviceDetail: isHardware
          ? [formData.mobileDeviceType, formData.deviceBrand, formData.deviceModel].filter(Boolean).join(' — ')
          : formData.serviceDetail,
        issue: formData.issueDescription,
        status: 'Pending',
        createdAt: new Date().toISOString(),
        osTicketNumber: osTicketNumber || null,
      };

      // Always save to localStorage so admin sees it even if Firestore is slow
      try {
        const local = JSON.parse(localStorage.getItem('serviceRequests') || '[]');
        local.unshift(newRequest);
        localStorage.setItem('serviceRequests', JSON.stringify(local));
      } catch (_) {}

      // Save to Firestore (cross-browser sync)
      try {
        await saveServiceRequest(newRequest);
      } catch (firestoreErr) {
        console.error('Firestore save failed — data kept in localStorage:', firestoreErr);
      }

      // Send confirmation email to client + internal alert
      try {
        const serviceSummary = buildServiceSummary();
        const fullName = `${formData.firstName} ${formData.lastName}`;
        const refId = newRequest.id;

        await notifyCustomer(
          formData.email,
          `Booking Confirmed — ${serviceSummary} | AkdmeTech`,
          bookingConfirmationEmail({
            firstName:   formData.firstName,
            refId,
            service:     serviceSummary,
            description: formData.issueDescription,
            date:        formData.preferredDate,
            time:        formData.preferredTime,
          }),
        );

        await notifyInternal(
          `New Booking — ${serviceSummary}`,
          `Customer: ${fullName} <${formData.email}>\nPhone: ${formData.phone}\n\nService: ${serviceSummary}\nDescription: ${formData.issueDescription}\nAppointment: ${formData.preferredDate} at ${formData.preferredTime}\nReference: ${refId}${osTicketNumber ? `\nosTicket #${osTicketNumber}` : ''}`,
          formData.email,
        );
      } catch (emailErr) {
        console.error('Booking email failed:', emailErr);
      }

      setLoading(false);
      setSnackbar({
        open: true,
        message: osTicketNumber
          ? `Booked! Your helpdesk ticket number is #${osTicketNumber}. Check status at /track-repair.`
          : 'Appointment booked! We will contact you shortly.',
        severity: 'success',
      });

      setFormData({
        firstName: '', lastName: '', email: '', phone: '',
        service: '', serviceDetail: '', mobileDeviceType: '',
        deviceBrand: '', deviceModel: '', issueDescription: '',
        preferredDate: '', preferredTime: '',
      });
    } catch (error) {
      setLoading(false);
      setSnackbar({
        open: true,
        message: error.message === 'Please fill in all required fields'
          ? error.message
          : 'There was an error booking your appointment. Please try again.',
        severity: 'error',
      });
    }
  };

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        width: '100%',
        mx: 'auto',
        p: { xs: 3, sm: 4 },
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.1) 100%)',
        backdropFilter: 'blur(10px)',
        borderRadius: '15px',
        boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        '& .MuiInputBase-root': {
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(5px)',
          borderRadius: 1,
          '&:hover': { background: 'rgba(255, 255, 255, 0.08)' },
          '&.Mui-focused': { background: 'rgba(255, 255, 255, 0.1)' },
        },
        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
        '& .MuiInputLabel-root': { color: 'text.secondary' },
      }}
    >
      <Typography
        variant="h4"
        component="h2"
        gutterBottom
        sx={{
          background: 'linear-gradient(45deg, #ff6b6b 30%, #ffd93d 90%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          mb: { xs: 3, sm: 4 },
          textAlign: 'center',
          fontSize: { xs: '1.75rem', sm: '2.125rem' },
        }}
      >
        Book a Service
      </Typography>

      <Grid container spacing={3}>
        {/* Contact info */}
        <Grid item xs={12} sm={6}>
          <TextField fullWidth required name="firstName" label="First Name" value={formData.firstName} onChange={handleChange} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth required name="lastName" label="Last Name" value={formData.lastName} onChange={handleChange} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth required type="email" name="email" label="Email" value={formData.email} onChange={handleChange} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth required name="phone" label="Phone Number" value={formData.phone} onChange={handleChange} />
        </Grid>

        {/* Service selector */}
        <Grid item xs={12}>
          <FormControl fullWidth required>
            <InputLabel>Service</InputLabel>
            <Select name="service" value={formData.service} onChange={handleChange} label="Service">
              {allServices.map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Hardware: brand + optional mobile device type */}
        {isHardware && (
          <>
            {isMobile && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Device Type</InputLabel>
                  <Select name="mobileDeviceType" value={formData.mobileDeviceType} onChange={handleChange} label="Device Type">
                    {mobileDeviceTypes.map((t) => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sm={isMobile ? 6 : 12}>
              <FormControl fullWidth required>
                <InputLabel>Brand</InputLabel>
                <Select name="deviceBrand" value={formData.deviceBrand} onChange={handleChange} label="Brand">
                  {(hardwareBrands[formData.service] || []).map((b) => (
                    <MenuItem key={b} value={b}>{b}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth name="deviceModel" label="Model (Optional)" value={formData.deviceModel} onChange={handleChange} />
            </Grid>
          </>
        )}

        {/* Non-hardware: contextual sub-options */}
        {!isHardware && formData.service && (
          <Grid item xs={12}>
            <FormControl fullWidth required>
              <InputLabel>Service Type</InputLabel>
              <Select name="serviceDetail" value={formData.serviceDetail} onChange={handleChange} label="Service Type">
                {(serviceDetails[formData.service] || []).map((d) => (
                  <MenuItem key={d} value={d}>{d}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        {/* Description */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            multiline
            rows={4}
            name="issueDescription"
            label={isHardware ? 'Describe the Issue' : 'Describe what you need'}
            value={formData.issueDescription}
            onChange={handleChange}
          />
        </Grid>

        {/* Date & Time */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth required type="date" name="preferredDate"
            label="Preferred Date" value={formData.preferredDate}
            onChange={handleChange} InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth required type="time" name="preferredTime"
            label="Preferred Time" value={formData.preferredTime}
            onChange={handleChange} InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12}>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading}
            sx={{
              background: 'linear-gradient(45deg, #ff6b6b 30%, #ffd93d 90%)',
              color: 'white',
              height: 56,
              fontSize: '1.1rem',
              position: 'relative',
              mt: 2,
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': { background: 'linear-gradient(45deg, #ff5252 30%, #ffd93d 90%)' },
            }}
          >
            {loading ? (
              <CircularProgress size={24} sx={{ color: 'white', position: 'absolute', top: '50%', left: '50%', marginTop: '-12px', marginLeft: '-12px' }} />
            ) : 'Book Service'}
          </Button>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            width: '100%',
            backdropFilter: 'blur(10px)',
            background: snackbar.severity === 'success'
              ? 'linear-gradient(135deg, rgba(76, 175, 80, 0.9) 0%, rgba(76, 175, 80, 0.8) 100%)'
              : 'linear-gradient(135deg, rgba(211, 47, 47, 0.9) 0%, rgba(211, 47, 47, 0.8) 100%)',
            color: 'white',
            '& .MuiAlert-icon': { color: 'white' },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BookingForm;
